import React from "react";
import { Box, Button } from "@mui/material";
import tokens from "../../theme/tokens";
import { INTERVIEWS_ENABLED } from "../../config/features";

const { color, font, radius } = tokens;

/**
 * The pipeline, left to right: candidates arrive in Resume Review, move to Screening when called,
 * and leave for one of the three decisions. All comes LAST because it is not a peer of the others —
 * it's their union. The first five partition the candidates; every one of them is in exactly one.
 */
export const STAGE_TABS = [
  { key: "review", label: "Resume Review" },
  { key: "screening", label: "Screening" },
  { key: "shortlist", label: "Shortlist" },
  { key: "backlog", label: "Backlog" },
  { key: "rejected", label: "Rejected" },
  // Dropped while the pipeline is screening-only. Console redirects ?stage=interview
  // to "all" so an old link doesn't land on a tab that no longer exists.
  ...(INTERVIEWS_ENABLED ? [{ key: "interview", label: "Interview" }] : []),
  { key: "all", label: "All" },
];

const chipSx = {
  minWidth: 0,
  px: 1.25,
  py: 0.375,
  border: `1px solid ${color.borderInput}`,
  borderRadius: `${radius.button}px`,
  font: `500 11.5px ${font.sans}`,
  color: color.textSecondary,
};

/**
 * Tab lives in the URL (?stage=…) so views are linkable — this is what replaces the
 * old separate /screening and /interviews routes. Switching is a pure client-side
 * filter: no refetch, and selection + scroll survive.
 */
const StageTabs = ({ active, counts, onChange, onSelectTop, selectableCount, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: { xs: 1, md: 2.5 },
      px: 2.25,
      py: { xs: 1.25, md: 0 },
      // Mobile: pills that scroll horizontally instead of an underlined tab strip.
      overflowX: { xs: "auto", md: "visible" },
      borderBottom: `1px solid ${color.border}`,
      "&::-webkit-scrollbar": { display: "none" },
    }}
  >
    {STAGE_TABS.map((tab) => {
      const isActive = active === tab.key;
      return (
        <Box
          key={tab.key}
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(tab.key)}
          sx={{
            flex: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
            font: `${isActive ? 600 : 500} 12.5px ${font.sans}`,
            // Pill on mobile…
            px: { xs: 1.625, md: 0 },
            py: { xs: 0.75, md: 1.5 },
            borderRadius: { xs: `${radius.pill}px`, md: 0 },
            border: { xs: `1px solid ${isActive ? color.indigo : color.borderInput}`, md: "none" },
            bgcolor: { xs: isActive ? color.indigo : color.surface, md: "transparent" },
            color: {
              xs: isActive ? "#fff" : color.textSecondary,
              // …underline on desktop.
              md: isActive ? color.indigo : color.textSecondary,
            },
            borderBottom: {
              md: `2px solid ${isActive ? color.indigo : "transparent"}`,
            },
            mb: { md: "-1px" },
          }}
        >
          {tab.label}{" "}
          <Box component="span" sx={{ font: `500 12px ${font.mono}` }}>
            {counts[tab.key] ?? 0}
          </Box>
        </Box>
      );
    })}

    <Box sx={{ ml: "auto", display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
      <Button sx={chipSx} disabled={!selectableCount} onClick={() => onSelectTop(5)}>
        Top 5
      </Button>
      <Button sx={chipSx} disabled={!selectableCount} onClick={() => onSelectTop(10)}>
        Top 10
      </Button>
      {action}
    </Box>
  </Box>
);

export default StageTabs;
